import { RolePermissions } from '@/lib/api/common/interfaces/role.interface'
import { useAuth } from '@/store/hooks'

function checkPermission(
    permissions: RolePermissions | undefined | null,
    module: string,
    resource: string,
    action: string
): boolean {
    if (!permissions || typeof permissions !== 'object') return false

    // Acesso total
    if ((permissions as any)['*'] === '*') return true

    const modulePerms = permissions[module]
    if (!modulePerms) return false

    // Acesso total ao módulo
    if (modulePerms === '*') return true

    if (typeof modulePerms !== 'object') return false

    const resourcePerms = (modulePerms as Record<string, string[] | '*'>)[
        resource
    ]
    if (!resourcePerms) return false

    // Acesso total ao recurso
    if (resourcePerms === '*') return true

    // Verificar ação específica
    return Array.isArray(resourcePerms) && resourcePerms.includes(action)
}

function checkModuleAccess(
    permissions: RolePermissions | undefined | null,
    module: string
): boolean {
    if (!permissions || typeof permissions !== 'object') return false
    if ((permissions as any)['*'] === '*') return true
    return !!permissions[module]
}

export function usePermission() {
    const { user, isMaster, isCompany } = useAuth()

    // Módulos disponíveis do plano (resolvidos pelo backend via permissions-map armazenado em user.planModules)
    const planModules = user?.planModules as Record<string, string[]> | undefined

    // Verifica se o módulo está disponível no plano
    const isModuleInPlan = (module: string): boolean => {
        if (isMaster) return true
        // Se não há planModules carregado, permitir (o backend já filtra via role)
        if (!planModules || typeof planModules !== 'object') return true
        return module in planModules
    }

    // Verifica se o recurso está disponível no plano
    const isResourceInPlan = (module: string, resource: string): boolean => {
        if (isMaster) return true
        if (!planModules || typeof planModules !== 'object') return true
        const resources = planModules[module]
        if (!resources) return false
        // [] = todos os recursos
        if (resources.length === 0) return true
        return resources.includes(resource)
    }

    const hasPermission = (
        module: string,
        resource: string,
        action: string
    ): boolean => {
        if (isMaster) return true
        if (!isModuleInPlan(module)) return false
        if (!isResourceInPlan(module, resource)) return false
        if (isCompany) return true
        return checkPermission(
            user?.role?.permissions as RolePermissions,
            module,
            resource,
            action
        )
    }

    const canAccessModule = (module: string): boolean => {
        if (isMaster) return true
        if (!isModuleInPlan(module)) return false
        if (isCompany) return true
        return checkModuleAccess(
            user?.role?.permissions as RolePermissions,
            module
        )
    }

    const canRead = (module: string, resource: string): boolean =>
        hasPermission(module, resource, 'read')

    const canCreate = (module: string, resource: string): boolean =>
        hasPermission(module, resource, 'create')

    const canUpdate = (module: string, resource: string): boolean =>
        hasPermission(module, resource, 'update')

    const canDelete = (module: string, resource: string): boolean =>
        hasPermission(module, resource, 'delete')

    return {
        hasPermission,
        canAccessModule,
        isModuleInPlan,
        isResourceInPlan,
        canRead,
        canCreate,
        canUpdate,
        canDelete,
    }
}
